import { isDefined, isNotDefined } from '@sgme/fp';
import { useContext } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import {
  FixingContext,
  useFixingSetIsModifyingField,
  useValidateFixing,
} from '@/App/tiles/orders/FixingOrder/FixingContext.tsx';
import { SgDatePicker } from '@/components/sg-design-system/components/input/SgDatePicker/index.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectFixingDate, selectFixingErrors } from '@/store/orders/fixing/selectors.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import { DATE_FORMAT_TEXT } from '@/utils/date.ts';

export const FixingDate = () => {
  const tileId = useContext(FixingContext).tileId;
  const setIsModifyingField = useFixingSetIsModifyingField();
  const track = useTrackEvent();

  const fixingDate = useAppSelector((state) => selectFixingDate(state, tileId));
  const errors = useAppSelector((state) => selectFixingErrors(state, tileId));
  const { locale } = useIntl();

  const { dateInputCultureInfo } = useAppSelector(getUserPreferences);

  const validateFixing = useValidateFixing();

  const handleDateChange = (date: string) => {
    track('Order action', 'Fixing date changed');
    if (isNotDefined(date)) {
      return;
    }

    validateFixing({
      fixingDateUtc: date,
    });
  };

  const getComputedLocale = () => {
    if (dateInputCultureInfo === 'en') {
      return 'en-US';
    }
    if (dateInputCultureInfo === 'fr') {
      return 'fr-FR';
    }
    if (locale === 'en') {
      return 'en-GB';
    }
    return 'fr-FR';
  };

  const expiryDateError = errors?.fixingDateUtc;

  return (
    <OrderRow labelId="order.fields.fixingDate" alignTop>
      <div
        className="d-flex flex-column flex-nowrap  gap-4px align-items-between justify-content-between flex-grow-0 flex-shrink-0"
        data-e2e="fixing-date"
      >
        <OmsFieldErrorWrapper errors={expiryDateError}>
          <SgDatePicker
            size="sm"
            yearControls
            value={fixingDate}
            placeholder="Fixing date"
            onChange={handleDateChange}
            locale={locale === 'en' ? 'en-GB' : 'fr-FR'}
            dateInputCultureInfo={getComputedLocale()}
            showClearButton={false}
            onFocus={() => setIsModifyingField(true)}
            onBlurInput={() => setIsModifyingField(false)}
            disablePresets={{ past: true, weekends: true }}
            validationState={isDefined(expiryDateError) ? 'invalid' : undefined}
            dateFormat={DATE_FORMAT_TEXT}
          />
        </OmsFieldErrorWrapper>
      </div>
    </OrderRow>
  );
};
